// src/pages/dashboard/resources/RoadmapTab.tsx
import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// Global types are now in src/vite-env.d.ts, so no 'declare global' here.

const RoadmapTab = () => {
  const { user, profile, loading: authLoading } = useAuth();
  const initAttemptedForCurrentUserRef = useRef(false);

  // window.__featurebase_widget_fully_ready tracks the readiness of the *embed* widget on this page.
  // window.__last_fb_user_id and window.__last_fb_profile_name help detect user context changes for the embed.

  const featurebaseCallback = useCallback((err: Error | null, event?: { action: string }) => {
    console.log("RoadmapPage: Featurebase embed callback. Error:", err, "Event:", event);
    if (err) {
      console.error("RoadmapPage: Featurebase embed initialization error:", err);
      const errorMessage = err.message || 'Unknown error initializing roadmap.';
      toast.error(`Roadmap portal error: ${errorMessage}`);
      window.__featurebase_widget_fully_ready = false; // Embed widget not ready
      initAttemptedForCurrentUserRef.current = false;
      return;
    }

    if (event?.action === 'widgetReady') {
      console.log('RoadmapPage: Featurebase embed "widgetReady" event. Content should be usable.');
      window.__featurebase_widget_fully_ready = true; // Embed widget is ready
      if (user) window.__last_fb_user_id = user.id;
      if (profile) window.__last_fb_profile_name = profile.full_name ?? undefined;
    } else if (event?.action === 'loaded') {
      console.log("RoadmapPage: Featurebase 'loaded' event. Waiting for 'widgetReady' for full interactivity.");
    } else if (event) {
        console.log("RoadmapPage: Featurebase event received (not widgetReady or loaded):", event);
    }
  }, [user, profile]);

  const initializeFeaturebaseEmbed = useCallback(() => {
    if (!user || !profile) {
        console.warn("RoadmapPage: initializeFeaturebaseEmbed called without user or profile. Aborting.");
        return;
    }

    // The SDK script from index.html should have loaded window.Featurebase.
    if (typeof window.Featurebase !== 'function') {
      console.error('RoadmapPage: Featurebase SDK (window.Featurebase) is not a function. Cannot initialize embed.');
      toast.error("Featurebase integration failed: SDK function not available on Roadmap page.");
      // This component will not attempt to load the SDK; it relies on the global one.
      return;
    }

    console.log("RoadmapPage: Calling window.Featurebase('embed')");
    initAttemptedForCurrentUserRef.current = true;

    const userFeaturebaseMetadata: Record<string, unknown> = {
      email: user.email,
      user_id: user.id,
      name: profile.full_name ?? undefined,
    };
    
    window.Featurebase('embed', {
      organization: 'scheduleheroai', // Ensure this is your Featurebase organization ID
      embedOptions: { 
        path: '/changelog' 
      }, 
      stylingOptions: { 
        theme: 'light', 
        hideMenu: true, 
        hideLogo: true 
      },
      user: { 
        metadata: userFeaturebaseMetadata 
      },
    }, featurebaseCallback);

  }, [user, profile, featurebaseCallback]);


  useEffect(() => {
    console.log("RoadmapPage: useEffect triggered. AuthLoading:", authLoading, "User:", !!user, "Profile:", !!profile);

    if (authLoading || !user || !profile) {
      console.log("RoadmapPage: Waiting for authentication and user/profile data to initialize embed.");
      return;
    }
    
    const currentProfileName = profile.full_name ?? undefined;
    const userContextChanged = user.id !== window.__last_fb_user_id || currentProfileName !== window.__last_fb_profile_name;

    if (userContextChanged) {
        console.log("RoadmapPage: User context changed for embed. Resetting Featurebase embed state.");
        window.__featurebase_widget_fully_ready = false;
        initAttemptedForCurrentUserRef.current = false;
    }

    if (window.__featurebase_widget_fully_ready && !userContextChanged) {
      console.log("RoadmapPage: Featurebase embed widget already marked as fully ready and user context unchanged.");
      return;
    }
    
    if (initAttemptedForCurrentUserRef.current && !window.__featurebase_widget_fully_ready) {
        console.log("RoadmapPage: Init attempt was made for current user for embed, but widget not ready yet. Waiting for callback or next effect run.");
        return;
    }

    // Check if the SDK is available before trying to initialize.
    if (typeof window.Featurebase === 'function') {
        console.log("RoadmapPage: Featurebase SDK function available. Initializing embed.");
        initializeFeaturebaseEmbed();
    } else {
        console.warn("RoadmapPage: window.Featurebase is not a function. SDK might still be loading or failed to load from index.html. Embed initialization deferred.");
        // If window.Featurebase is still the stub (e.g., window.Featurebase.q exists), it means the script is loading.
        // If window.Featurebase is undefined, the script in index.html might have failed.
        if (window.Featurebase && Array.isArray(window.Featurebase.q)) {
            console.log("RoadmapPage: Featurebase stub (queue) exists. SDK is loading.");
        } else if (typeof window.Featurebase === 'undefined') {
            console.error("RoadmapPage: FATAL - window.Featurebase is undefined. SDK script from index.html did not create the stub or load.");
        }
    }

  }, [user, profile, authLoading, initializeFeaturebaseEmbed]);

  useEffect(() => {
    // Cleanup effect for when the component unmounts.
    return () => {
      console.log("RoadmapPage: Unmounting. Current embed widget ready state:", window.__featurebase_widget_fully_ready);
      // If Featurebase provided a way to destroy/cleanup embed widgets, it would go here.
      // For now, the flags __last_fb_user_id etc. will be reset if a new user logs in or profile changes.
    };
  }, []);


  return (
    <div className="w-full h-full relative">
      <div 
        data-featurebase-embed 
        className="w-full h-full"
      >
        {/* Featurebase content will be embedded here by the SDK */}
      </div>
    </div>
  );
};

export default RoadmapTab;
